/**
 * Kod renklendirme - `highlight.js/lib/core` + ACIK dil kaydi.
 *
 * ## Neden full build DEGIL
 *
 * `highlight.js`in tamami 190+ dil ve vendored halde diskte 121.727 byte.
 * Cekirdek + on dil ~25 KB gz. Kullanilmayan 180 dili tasimak, bundle
 * butcesinin (ilk JS <= 180 KB gz) tek basina yedide birini yiyecekti.
 *
 * ## Neden `shiki` DEGIL
 *
 * Reddedildi: 2 MB+ TextMate grammar, WASM, ve **async-only API**. Son
 * ucu belirleyici - frame basina cagrilan bir streaming renderer'i icin
 * yanlis sekil.
 *
 * ## Akis sirasinda TAIL'de ATLANIYOR
 *
 * Kapanmamis bir fence icindeki kod, fence kapanana kadar duz metin. Bunu
 * her frame yeniden renklendirmek vanilla arayuzdeki titremenin sebebiydi:
 * yarim kalan kod blogu her token'da farkli renkleniyordu.
 */

import hljs from 'highlight.js/lib/core'
import i18next from 'i18next'
import bash from 'highlight.js/lib/languages/bash'
import css from 'highlight.js/lib/languages/css'
import diff from 'highlight.js/lib/languages/diff'
import json from 'highlight.js/lib/languages/json'
import javascript from 'highlight.js/lib/languages/javascript'
import python from 'highlight.js/lib/languages/python'
import sql from 'highlight.js/lib/languages/sql'
import typescript from 'highlight.js/lib/languages/typescript'
import xml from 'highlight.js/lib/languages/xml'

import { writeClipboard } from '../components/CopyButton'

// Diller ACIKCA kaydediliyor. Liste, bu uygulamada gercekten uretilen kodu
// kapsiyor: `xml` HTML'i de kapsiyor (hljs'de HTML `xml` altinda).
const LANGUAGES = { bash, css, diff, javascript, json, python, sql, typescript, xml }

let registered = false

function ensureRegistered(): void {
  if (registered) return
  registered = true
  for (const [name, definition] of Object.entries(LANGUAGES)) {
    hljs.registerLanguage(name, definition)
  }
  // Yaygin takma adlar: model `js`, `ts`, `py`, `sh`, `html` yaziyor.
  hljs.registerAliases(['js'], { languageName: 'javascript' })
  hljs.registerAliases(['ts', 'tsx'], { languageName: 'typescript' })
  hljs.registerAliases(['py'], { languageName: 'python' })
  hljs.registerAliases(['sh', 'shell', 'ps1', 'powershell'], { languageName: 'bash' })
  hljs.registerAliases(['html'], { languageName: 'xml' })
}

/** Bu dil kayitli mi? Bilinmeyen dilde renklendirme ATLANIYOR. */
export function isSupported(language: string): boolean {
  ensureRegistered()
  return Boolean(hljs.getLanguage(language.toLowerCase()))
}

/**
 * Sanitize edilmis DOM'daki kod bloklarini renklendirir.
 *
 * `hljs.highlightElement` KULLANILMIYOR: o, elemanin `innerHTML`ini
 * dogrudan yaziyor ve `class` disinda attribute eklemiyor - ama biz
 * ciktinin allowlist'imizle uyumlu oldugunu bilmek istiyoruz. `highlight()`
 * ile string uretip yaziyoruz ve girdiyi `textContent`ten aliyoruz, yani
 * hic HTML yeniden ayristirilmiyor.
 */
export function highlightIn(root: HTMLElement): void {
  const blocks = root.querySelectorAll<HTMLElement>('pre > code')
  if (blocks.length === 0) return
  ensureRegistered()

  for (const block of blocks) {
    if (block.dataset.highlighted !== 'true') {
      const language = languageOf(block)
      if (!language || !isSupported(language)) {
        // Renklendirme yok ama blok yine de `<pre>` icinde okunabilir.
        block.dataset.highlighted = 'true'
      } else {
        try {
          const result = hljs.highlight(block.textContent ?? '', {
            language,
            // Bozuk kodda hata firlatmasin: yanit okunabilir kalmali.
            ignoreIllegals: true,
          })
          block.innerHTML = result.value
          block.dataset.highlighted = 'true'
        } catch {
          // hljs bir dilde patlarsa mesajin geri kalani etkilenmemeli.
          block.dataset.highlighted = 'true'
        }
      }
    }

    // Dilden BAGIMSIZ: renklendirilemeyen (bilinmeyen dil, `language-`
    // sinifi hic olmayan duz ``` ```) bloklar da dahil HER `<pre>` bir
    // kopyalama dugmesi alir - kullanicinin istedigi tam olarak bu.
    addCopyButton(block)
    markScrollable(block)
  }
}

/**
 * Yatay tasan bir kod blogunun sag kenarina soluk bir ipucu ekler -
 * ozellikle mobilde, uzun bir satirin kirpildigini (kaydirilamaz)
 * sandiran bir gorunumu onluyor. Sadece GERCEKTEN tasan bloklara
 * ekleniyor; kisa kod boyle bir ipucuna ihtiyac duymuyor.
 */
function markScrollable(block: HTMLElement): void {
  const pre = block.parentElement
  if (!pre || pre.tagName !== 'PRE') return
  const overflowing = pre.scrollWidth > pre.clientWidth + 1
  pre.classList.toggle('md-pre-scrollable', overflowing)
}

/**
 * Bir kod blogunun `<pre>` ebeveynine kopyalama dugmesi ekler - React
 * DEGIL: bu DOM, `dangerouslySetInnerHTML` ile yazilan sanitize edilmis
 * HTML'in ICINDE, React agacinin disinda (bkz. `Markdown.tsx` dosya basi
 * notu). O yuzden `CopyButton` bileseni yerine `CopyButton.tsx`teki AYNI
 * `writeClipboard` yardimcisini kullanan duz bir DOM dugmesi.
 *
 * `pre.dataset.copyReady` ile idempotent: `highlightIn` akis sirasinda
 * ayni bloklar icin tekrar tekrar cagriliyor (kararli onek DEGISMEDIKCE
 * yeniden calismiyor ama kuyruk HER FRAME calisiyor) - dugme BIR KEZ
 * eklenmeli.
 */
function addCopyButton(block: HTMLElement): void {
  const pre = block.parentElement
  if (!pre || pre.tagName !== 'PRE' || pre.dataset.copyReady === 'true') return
  pre.dataset.copyReady = 'true'

  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'md-pre-copy'
  button.setAttribute('aria-live', 'polite')
  button.innerHTML = COPY_ICON_SVG
  button.setAttribute('aria-label', i18next.t('common.copy'))

  let resetTimer: ReturnType<typeof setTimeout> | undefined
  button.addEventListener('click', () => {
    void writeClipboard(block.textContent ?? '').then((ok) => {
      if (!ok) return
      clearTimeout(resetTimer)
      button.innerHTML = CHECK_ICON_SVG
      button.setAttribute('aria-label', i18next.t('common.copied'))
      resetTimer = setTimeout(() => {
        button.innerHTML = COPY_ICON_SVG
        button.setAttribute('aria-label', i18next.t('common.copy'))
      }, 1500)
    })
  })

  pre.appendChild(button)
}

// `components/icons.tsx`teki `CopyIcon`/`CheckIcon` ile AYNI yol verileri -
// burada React DEGIL duz DOM oldugu icin string olarak tekrarlaniyor.
const COPY_ICON_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<rect x="8.5" y="8.5" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.6"/>' +
  '<path d="M15.5 8.5V5.5a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>'
const CHECK_ICON_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M5 12.5 9.5 17 19 6.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>'

/**
 * `marked`in urettigi `language-xxx` sinifindan dili cikarir.
 *
 * `hljs.highlightAuto` KULLANILMIYOR: otomatik tespit kisa parcalarda
 * sistematik olarak yanlis ve akis sirasinda blok buyudukce tespit
 * DEGISIYOR - yani ayni kod her frame farkli renkleniyor.
 */
function languageOf(block: HTMLElement): string | null {
  for (const name of block.classList) {
    if (name.startsWith('language-')) return name.slice('language-'.length)
  }
  return null
}
